// SPECULO telemetry — swappable config, fire-and-forget.
//
// The endpoint URL, app id and API key can all be overridden with build-time
// environment variables so we can swap to a real SPECULO deployment without a
// code change. Resolution order for the endpoint:
//   1. `REACT_APP_SPECULO_URL` — explicit override (external SPECULO host).
//   2. `REACT_APP_API_URL`     — active host backend (deploy-agnostic).
//   3. `REACT_APP_BACKEND_URL` — preview/prod backend URL (fallback).
//   4. `/api/v1/telemetry/ping` — relative path (dev / same-origin).
//
// HONESTY / PLACEHOLDER STATUS:
//   `sk_speculo_live_key_here` is a placeholder — the *external* SPECULO
//   service is NOT active yet. The dispatch target is our OWN backend on
//   the active host (real Mongo sink at `/api/v1/telemetry/ping`), which
//   validates the same X-API-Key server-side. Documented in
//   /app/memory/test_credentials.md.
//
// Guarantees:
//   - Never throws.
//   - Never blocks the caller (fire-and-forget with `keepalive: true`).
//   - Logs at most one `console.debug` per failed call (no spam, no toasts).

function _resolveDefaultUrl() {
  const clean = (v) => (v || "").replace(/\/+$/, "");
  const apiUrl = clean(process.env.REACT_APP_API_URL);
  if (apiUrl) return `${apiUrl}/api/v1/telemetry/ping`;
  const backend = clean(process.env.REACT_APP_BACKEND_URL);
  if (backend) return `${backend}/api/v1/telemetry/ping`;
  return "/api/v1/telemetry/ping";
}

const DEFAULT_URL = _resolveDefaultUrl();

const SPECULO_URL = (process.env.REACT_APP_SPECULO_URL || DEFAULT_URL).trim();
const APP_ID = (process.env.REACT_APP_SPECULO_APP_ID || "app_5580eff272ded").trim();
const API_KEY = (process.env.REACT_APP_SPECULO_KEY || "sk_speculo_live_key_here").trim();

let sessionStart = null;
let appOpenSent = false;

/** Called by AuthContext on transition to authed. Resets per-login state. */
export function beginTelemetrySession() {
  sessionStart = Date.now();
  appOpenSent = false;
}

/** Called by AuthContext on logout / auth teardown. */
export function endTelemetrySession() {
  sessionStart = null;
  appOpenSent = false;
}

function sessionDurationSeconds() {
  if (!sessionStart) return 0;
  return Math.round((Date.now() - sessionStart) / 1000);
}

/**
 * Fire a single ping. Fire-and-forget: does NOT return the promise to the
 * caller, and any error is silently swallowed after one debug log.
 */
function sendPing({ user_id, event_type }) {
  if (!SPECULO_URL || !APP_ID || !API_KEY) return;
  // SOS-mode discipline: never fire ANY HTTP from the frontend when the
  // browser believes it's offline. Avoids wasted bandwidth / log noise and
  // honors the "zero network when offline" contract of the SOS panel.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const body = {
    app_id: APP_ID,
    user_id: user_id || null,
    session_duration: sessionDurationSeconds(),
    event_type,
  };
  try {
    // Fire-and-forget — no await, no bubble-up. keepalive lets it complete
    // even if the tab/nav is closing (crucial for message_send pings).
    fetch(SPECULO_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": API_KEY,
      },
      body: JSON.stringify(body),
      keepalive: true,
      // Never send cookies (this is a metrics endpoint, not an auth surface).
      credentials: "omit",
      mode: "cors",
    }).catch((err) => {
      try {
        console.debug("[telemetry] ping failed:", event_type, err?.message || err);
      } catch (_e) {
        /* ignore */
      }
    });
  } catch (err) {
    try {
      console.debug("[telemetry] fetch threw:", err?.message || err);
    } catch (_e) {
      /* ignore */
    }
  }
}

/** Fire the "app_open" ping (once per authenticated session). */
export function trackAppOpen(userId) {
  if (appOpenSent) return;
  appOpenSent = true;
  if (!sessionStart) sessionStart = Date.now();
  sendPing({ user_id: userId, event_type: "app_open" });
}

/** Fire a "message_send" ping. Called from every send flow. */
export function trackMessageSend(userId) {
  sendPing({ user_id: userId, event_type: "message_send" });
}

/** Fire a "signup" ping on successful account creation (email OR Google). */
export function trackSignup(userId) {
  // Ensure the session clock has started even before AuthContext beginTelemetrySession.
  if (!sessionStart) sessionStart = Date.now();
  sendPing({ user_id: userId, event_type: "signup" });
}

/** Exposed for smoke tests / debugging. */
export const telemetryConfig = {
  url: SPECULO_URL,
  app_id: APP_ID,
  api_key_placeholder: API_KEY === "sk_speculo_live_key_here",
  // Which env var actually resolved the URL — useful for post-deploy sanity.
  source:
    process.env.REACT_APP_SPECULO_URL
      ? "SPECULO_URL"
      : process.env.REACT_APP_API_URL
      ? "API_URL"
      : process.env.REACT_APP_BACKEND_URL
      ? "BACKEND_URL"
      : "relative",
};
